import { useEffect, useState, useCallback } from "react";
import { getAdminStatsOverview } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

function formatKRW(n) {
  if (n == null) return "-";
  return n.toLocaleString() + "원";
}

function formatNumber(n) {
  if (n == null) return "-";
  return n.toLocaleString();
}

function formatElapsedShort(ms) {
  if (!ms || !Number.isFinite(ms)) return "-";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}분`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}시간 ${mins % 60}분`;
  const days = Math.floor(hrs / 24);
  return `${days}일 ${hrs % 24}시간`;
}

const PLAN_LABEL = {
  once: "단건",
  basic: "베이직",
  pro: "프로",
  trial: "체험",
  pkg6: "6회 패키지",
  pkg12: "12회 패키지",
  unknown: "기타",
};

export default function AdminStats({ onSignOut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getAdminStatsOverview();
      setData(d);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin">
      <AdminSidebar active="stats" onSignOut={onSignOut}/>

      <main className="admin-main">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:6,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 className="admin-h1">📊 통계</h1>
            <p className="admin-sub">
              이번 달 KPI · 최근 30일 시계열 · 매출 분포 · AI 비용
            </p>
          </div>
          <button className="btn btn-outline" style={{padding:'8px 16px',fontSize:12}} onClick={load}>
            ⟳ 새로고침
          </button>
        </div>

        {error && (
          <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#c0392b'}}>
            통계를 불러오지 못했어요: {error}
          </div>
        )}

        {loading || !data ? (
          <div style={{textAlign:'center',padding:'80px 0'}}>
            <div className="spinner" style={{display:'inline-block'}}/>
          </div>
        ) : (
          <>
            {/* 이번 달 KPI */}
            <div className="admin-stats" style={{gridTemplateColumns:'repeat(4, 1fr)'}}>
              <Kpi label="이번 달 신규 케이스" value={data.kpi.monthNewCases} tone="#2c5f8d" sub="status=received~done"/>
              <Kpi label="이번 달 발행" value={data.kpi.monthDoneCases} tone="#1f7a3f" sub={`평균 ${formatElapsedShort(data.kpi.avgPublishMs)} 소요`}/>
              <Kpi label="이번 달 신규 보호자" value={data.kpi.monthNewUsers} tone="#6c5ce7"/>
              <Kpi label="이번 달 매출" value={formatKRW(data.kpi.monthRevenue)} tone="#e6852b"/>
            </div>

            {/* 최근 30일 일별 차트 */}
            <Section title="📈 최근 30일 일별 — 접수 vs 발행">
              <DailyBarChart days={data.daily}/>
            </Section>

            <div style={{display:'grid',gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)',gap:16,alignItems:'flex-start'}}>
              {/* 플랜별 매출 */}
              <Section title="💳 최근 30일 매출 분포">
                <PlanRevenueList planRevenue={data.planRevenue}/>
              </Section>

              {/* 증상 태그 top */}
              <Section title="🩺 최근 30일 증상 태그 Top 8">
                <TagRankList tags={data.topTags}/>
              </Section>
            </div>

            {/* AI 비용 */}
            <Section title="🤖 이번 달 AI 사용량">
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:12}}>
                <SmallStat label="입력 토큰" value={formatNumber(data.kpi.monthTokensIn)}/>
                <SmallStat label="출력 토큰" value={formatNumber(data.kpi.monthTokensOut)}/>
                <SmallStat label="총 토큰" value={formatNumber((data.kpi.monthTokensIn ?? 0) + (data.kpi.monthTokensOut ?? 0))}/>
              </div>
              {Object.keys(data.modelTokens).length > 0 && (
                <div style={{marginTop:14}}>
                  <div style={{fontSize:11,color:'#718096',fontWeight:700,marginBottom:8}}>모델별 분배</div>
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {Object.entries(data.modelTokens).map(([model, t]) => (
                      <div key={model} style={{display:'grid',gridTemplateColumns:'1.2fr 60px 1fr 1fr',alignItems:'center',gap:10,padding:'8px 10px',background:'#fafafa',borderRadius:8,fontSize:12}}>
                        <div style={{fontWeight:700}}>{model}</div>
                        <div style={{color:'#718096',fontVariantNumeric:'tabular-nums'}}>{t.count}회</div>
                        <div style={{color:'#4a5568',fontVariantNumeric:'tabular-nums'}}>입 {formatNumber(t.in)}</div>
                        <div style={{color:'#4a5568',fontVariantNumeric:'tabular-nums'}}>출 {formatNumber(t.out)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{marginTop:10,fontSize:10,color:'#a0aec0',lineHeight:1.5}}>
                · Anthropic 모델별 단가 변동이 잦아서 USD 환산은 생략. 토큰 수만 노출.
              </div>
            </Section>
          </>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{background:'#fff',borderRadius:14,padding:'16px 18px',boxShadow:'var(--shadow-sm)',marginBottom:16}}>
      <div style={{fontSize:12,fontWeight:800,color:'#1a1a2e',marginBottom:12}}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div style={{background:'#fff',borderRadius:14,padding:'16px 18px',boxShadow:'var(--shadow-sm)'}}>
      <div style={{fontSize:11,color:'#718096',fontWeight:700,textTransform:'uppercase',letterSpacing:.4}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color:tone,marginTop:4,letterSpacing:-0.5,fontVariantNumeric:'tabular-nums'}}>
        {value}
      </div>
      {sub && <div style={{fontSize:11,color:'#718096',marginTop:2}}>{sub}</div>}
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div style={{padding:'10px 12px',background:'#fafafa',borderRadius:10}}>
      <div style={{fontSize:10,fontWeight:700,color:'#718096',textTransform:'uppercase',letterSpacing:.4}}>{label}</div>
      <div style={{fontSize:18,fontWeight:800,color:'#1a1a2e',marginTop:2,fontVariantNumeric:'tabular-nums'}}>{value}</div>
    </div>
  );
}

// 일별 막대 차트. 각 날짜에 접수(파란) 위에 발행(녹색) 막대를 겹쳐 그린다.
function DailyBarChart({ days }) {
  const maxVal = Math.max(1, ...days.map((d) => Math.max(d.received, d.done)));
  const width = 800;
  const height = 200;
  const padLeft = 32;
  const padBottom = 28;
  const padTop = 10;
  const padRight = 12;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const barW = chartW / days.length;
  const barInner = Math.max(2, barW * 0.36);

  // y축 눈금 3개
  const ticks = [0, Math.ceil(maxVal / 2), maxVal];

  return (
    <div style={{overflowX:'auto'}}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{width:'100%',minWidth:600,height,fontFamily:'inherit'}}>
        {ticks.map((v) => {
          const y = padTop + chartH - (v / maxVal) * chartH;
          return (
            <g key={v}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} stroke="#eee"/>
              <text x={padLeft - 6} y={y + 4} fontSize="10" fill="#a0aec0" textAnchor="end">{v}</text>
            </g>
          );
        })}

        {days.map((d, i) => {
          const xCenter = padLeft + i * barW + barW / 2;
          const recH = (d.received / maxVal) * chartH;
          const donH = (d.done / maxVal) * chartH;
          const recY = padTop + chartH - recH;
          const donY = padTop + chartH - donH;
          return (
            <g key={d.date}>
              <rect x={xCenter - barInner} y={recY} width={barInner * 0.9} height={recH}
                fill="#2c5f8d" opacity={0.65} rx={2}>
                <title>{`${d.date} · 접수 ${d.received}건`}</title>
              </rect>
              <rect x={xCenter + barInner * 0.05} y={donY} width={barInner * 0.9} height={donH}
                fill="#1f7a3f" opacity={0.75} rx={2}>
                <title>{`${d.date} · 발행 ${d.done}건`}</title>
              </rect>
            </g>
          );
        })}

        {/* x축 라벨 — 5일 간격 */}
        {days.map((d, i) => {
          if (i % 5 !== 0 && i !== days.length - 1) return null;
          const x = padLeft + i * barW + barW / 2;
          const md = d.date.slice(5).replace("-", "/");
          return (
            <text key={d.date} x={x} y={height - 10} fontSize="10" fill="#718096" textAnchor="middle">{md}</text>
          );
        })}

        {/* 축선 */}
        <line x1={padLeft} x2={width - padRight} y1={padTop + chartH} y2={padTop + chartH} stroke="#e2e8f0"/>
      </svg>
      <div style={{display:'flex',gap:14,fontSize:11,color:'#718096',marginTop:6}}>
        <div style={{display:'flex',alignItems:'center',gap:5}}>
          <span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'#2c5f8d',opacity:0.65}}/>
          접수
        </div>
        <div style={{display:'flex',alignItems:'center',gap:5}}>
          <span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'#1f7a3f',opacity:0.75}}/>
          발행
        </div>
      </div>
    </div>
  );
}

function PlanRevenueList({ planRevenue }) {
  const entries = Object.entries(planRevenue ?? {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return <div style={{fontSize:12,color:'#718096'}}>매출 데이터가 없어요.</div>;
  }
  const total = entries.reduce((acc, [, v]) => acc + v, 0);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      {entries.map(([plan, amount]) => {
        const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
        return (
          <div key={plan}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:3,fontSize:12}}>
              <div style={{fontWeight:700,color:'#1a1a2e'}}>{PLAN_LABEL[plan] ?? plan}</div>
              <div style={{fontVariantNumeric:'tabular-nums',color:'#4a5568'}}>
                {formatKRW(amount)} <span style={{color:'#a0aec0'}}>· {pct}%</span>
              </div>
            </div>
            <div style={{height:6,background:'#f1f5f9',borderRadius:3,overflow:'hidden'}}>
              <div style={{width:`${pct}%`,height:'100%',background:'#e6852b',borderRadius:3}}/>
            </div>
          </div>
        );
      })}
      <div style={{marginTop:6,fontSize:11,color:'#a0aec0',display:'flex',justifyContent:'space-between'}}>
        <span>합계</span>
        <span style={{fontVariantNumeric:'tabular-nums'}}>{formatKRW(total)}</span>
      </div>
    </div>
  );
}

function TagRankList({ tags }) {
  if (!tags?.length) {
    return <div style={{fontSize:12,color:'#718096'}}>증상 태그 데이터가 없어요.</div>;
  }
  const maxCount = Math.max(...tags.map((t) => t.count));
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      {tags.map((t) => {
        const pct = (t.count / maxCount) * 100;
        return (
          <div key={t.tag} style={{display:'grid',gridTemplateColumns:'1fr 50px',gap:10,alignItems:'center'}}>
            <div style={{position:'relative',height:24,background:'#f1f5f9',borderRadius:6,overflow:'hidden'}}>
              <div style={{position:'absolute',inset:0,width:`${pct}%`,background:'#9b89b333'}}/>
              <div style={{position:'absolute',inset:0,padding:'0 8px',display:'flex',alignItems:'center',fontSize:12,fontWeight:600,color:'#1a1a2e'}}>
                {t.tag}
              </div>
            </div>
            <div style={{textAlign:'right',fontSize:12,fontWeight:700,color:'#5e4a7f',fontVariantNumeric:'tabular-nums'}}>{t.count}</div>
          </div>
        );
      })}
    </div>
  );
}
